import { Injectable } from '@angular/core';
import { BulkProcessorSdkService } from '@vendasta/bulk-actions';
import moment from 'moment-timezone';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ApiService } from '../../api-service/api.service';
import { Market } from '../../core/markets/market';

interface BulkActionBaseRequest {
  actionListId: string;
  marketId?: string;
}

interface PauseCampaignRequest extends BulkActionBaseRequest {
  campaignId: string;
}

interface AddToCampaignRequest extends BulkActionBaseRequest {
  campaignId: string;
  scheduledDateTime?: string;
  fromName?: string;
  fromEmail?: string;
}

const formatDateToDateTime = (date: Date): string => moment(date).format('YYYY-MM-DD[T]HH:mm:ss[Z]');

interface ChangeMarketRequest extends BulkActionBaseRequest {
  newMarketId: string;
  newMarketName: string;
}

interface AddTagsRequest extends BulkActionBaseRequest {
  tags: string[];
}

export type BrandCreateOption = 'tag' | 'geo';

interface CreateBrandRequest extends BulkActionBaseRequest {
  brandName: string;
  actionListName: string;
  brandCreateOption: BrandCreateOption;
}

interface ActivateAppRequest extends BulkActionBaseRequest {
  productId: string;
  productName: string;
  numberOfAccountsToActivate: number;
  // optionally send either (editionId, editionName) OR (addonId, addonName)
  editionId?: string;
  editionName?: string;
  addonId?: string;
  addonName?: string;
}

interface EditionInfo {
  editionId: string;
  editionName?: string;
}

interface AddonInfo {
  addonId: string;
  addonName?: string;
}

interface CheckFeatureFlagRequest {
  actionListId: string;
  featureFlagId: string;
  useBulkActionsModelFlag?: boolean;
}

interface CheckFeatureFlagResponse {
  has_access: boolean;
}

@Injectable({ providedIn: 'root' })
export class ActionsApiService {
  constructor(private apiService: ApiService, private bulkProcessor: BulkProcessorSdkService) {}

  private activateAppBulkAction(
    listId: string,
    productId: string,
    productName: string,
    numAccounts: number,
    edition?: EditionInfo,
    addon?: AddonInfo,
  ): Observable<null | never> {
    const url = '/_ajax/v1/bulk-actions/product-activation/';
    const body: ActivateAppRequest = {
      actionListId: listId,
      productId: productId,
      productName: productName,
      numberOfAccountsToActivate: numAccounts,
    };
    if (!!edition && edition.editionId) {
      body.editionId = edition.editionId;
      body.editionName = edition.editionName;
    }
    if (!!addon && addon.addonId) {
      body.addonId = addon.addonId;
      body.addonName = addon.addonName;
    }
    return this.apiService.post(url, body);
  }

  /**
   * @deprecated use exportAccountNAPData exclusively once `bulk_actions_v2` feature is GA
   */
  public exportAccountData(actionListId: string): Observable<any> {
    const url = '/_ajax/v1/action-lists/export-nap-product/';
    const body = {
      actionListId: actionListId,
    };
    return this.apiService.post(url, body);
  }

  public exportAccountNAPData(actionListId: string, marketId?: string): Observable<null | never> {
    const url = '/_ajax/v1/bulk-actions/export-nap-product/';
    const body: BulkActionBaseRequest = {
      actionListId: actionListId,
    };
    if (marketId) {
      body.marketId = marketId;
    }
    return this.apiService.post(url, body);
  }

  /**
   * @deprecated use addListToCampaignBulkAction exclusively once `bulk_actions_v2` feature is GA
   */
  public addListToCampaign(
    campaignId: string,
    listId: string,
    scheduledStart?: Date,
    fromName?: string,
    fromEmail?: string,
  ): Observable<any> {
    const url = '/_ajax/v1/action-lists/add-to-campaign/';
    const body = {
      campaignId: campaignId,
      scheduledDateTime: null,
    } as any;
    if (listId.startsWith('G-')) {
      body.groupId = listId;
    } else {
      body.actionListId = listId;
    }
    if (scheduledStart) {
      body.scheduledDateTime = formatDateToDateTime(scheduledStart);
    }
    body.fromName = fromName;
    body.fromEmail = fromEmail;
    return this.apiService.post(url, body);
  }

  addListToCampaignBulkAction(
    campaignId: string,
    listId: string,
    scheduledStart?: Date,
    marketId?: string,
    fromName?: string,
    fromEmail?: string,
  ): Observable<null | never> {
    const url = '/_ajax/v1/bulk-actions/start-pipeline/add-to-campaign/';
    const body: AddToCampaignRequest = {
      campaignId: campaignId,
      actionListId: listId,
    };
    if (scheduledStart) {
      body.scheduledDateTime = formatDateToDateTime(scheduledStart);
    }
    if (marketId) {
      body.marketId = marketId;
    }
    if (!!fromName && !!fromEmail) {
      body.fromName = fromName;
      body.fromEmail = fromEmail;
    }
    return this.apiService.post(url, body);
  }

  /**
   * @deprecated use pauseListForCampaignBulkAction exclusively once `bulk_actions_v2` feature is GA
   */
  public pauseListForCampaign(campaignId: string, listId: string): Observable<any> {
    const url = '/_ajax/v1/action-lists/pause-campaign/';
    const body = {
      campaignId: campaignId,
    } as any;

    if (listId.startsWith('G-')) {
      body.groupId = listId;
    } else {
      body.actionListId = listId;
    }
    return this.apiService.post(url, body);
  }

  public pauseListForCampaignBulkAction(
    campaignId: string,
    listId: string,
    marketId?: string,
  ): Observable<null | never> {
    const url = '/_ajax/v1/bulk-actions/start-pipeline/pause-campaign/';
    const body: PauseCampaignRequest = {
      campaignId: campaignId,
      actionListId: listId,
    };
    if (marketId) {
      body.marketId = marketId;
    }
    return this.apiService.post(url, body);
  }

  public getSnapshotRefreshForListPreview(listId: string): Observable<any> {
    const url = '/_ajax/v1/action-lists/preview-refresh-snapshot/?actionListId=' + listId;
    return this.apiService.get(url).pipe(
      map((result) => {
        return {
          toCreate: result.create,
          toRefresh: result.refresh,
          stillCurrent: result.unaffected,
        };
      }),
    );
  }

  /**
   * @deprecated use createOrRefreshSnapshotsBulkAction exclusively once `bulk_actions_v2` feature is GA
   */
  public createOrRefreshSnapshotsForList(listId: string): Observable<any> {
    const url = '/_ajax/v1/action-lists/refresh-snapshot/';
    const body = {
      actionListId: listId,
    };
    return this.apiService.post(url, body);
  }

  public createOrRefreshSnapshotsBulkAction(listId: string, marketId?: string): Observable<null | never> {
    const url = '/_ajax/v1/bulk-actions/start-pipeline/refresh-snapshot/';
    const body: BulkActionBaseRequest = {
      actionListId: listId,
    };
    if (marketId) {
      body.marketId = marketId;
    }
    return this.apiService.post(url, body);
  }

  public getCountActiveProductsForList(listId: string, productId: string): Observable<number> {
    const url = '/_ajax/v1/action-lists/get-products/?actionListId=' + listId + '&productId=' + productId;
    return this.apiService.get(url).pipe(
      map((result) => {
        return result.active_accounts;
      }),
    );
  }

  /**
   * @deprecated use activateProductBulkAction exclusively once `bulk_actions_v2` feature is GA
   */
  public activateProductForList(
    listId: string,
    productId: string,
    productName: string,
    numAccounts: number,
    editionId: string,
    editionName: string,
  ): Observable<any> {
    const url = '/_ajax/v1/action-lists/product-activation/';
    const body = {
      productId: productId,
      productName: productName,
      numberOfAccountsToActivate: numAccounts,
      editionId: editionId,
      editionName: editionName,
    } as any;
    if (listId.startsWith('G-')) {
      body.groupId = listId;
    } else {
      body.actionListId = listId;
    }
    return this.apiService.post(url, body);
  }

  activateProductBulkAction(
    listId: string,
    productId: string,
    productName: string,
    numAccounts: number,
    editionId: string,
    editionName: string,
  ): Observable<null | never> {
    const edition: EditionInfo = { editionId: editionId, editionName: editionName };
    return this.activateAppBulkAction(listId, productId, productName, numAccounts, edition);
  }

  checkIfAccountsOnListHaveAccessToFeatureFlag(
    listId: string,
    featureFlag: string,
    useNewActionListModel?: boolean,
  ): Observable<boolean | never> {
    const url = '/_ajax/v1/action-list/check-feature-flag/';
    const body: CheckFeatureFlagRequest = {
      actionListId: listId,
      featureFlagId: featureFlag,
    };
    if (useNewActionListModel !== undefined && useNewActionListModel !== null) {
      body.useBulkActionsModelFlag = useNewActionListModel;
    }
    return this.apiService
      .post(url, body)
      .pipe(map((response: CheckFeatureFlagResponse): boolean => response.has_access));
  }

  /**
   * @deprecated use activateAddonBulkAction exclusively once `bulk_actions_v2` feature is GA
   */
  public activateAddonForList(
    listId: string,
    productId: string,
    productName: string,
    addonId: string,
    addonName: string,
    numAccounts: number,
  ): Observable<any> {
    const url = '/_ajax/v1/action-lists/product-activation/';
    const body = {
      actionListId: listId,
      productId: productId,
      productName: productName,
      addonId: addonId,
      addonName: addonName,
      numberOfAccountsToActivate: numAccounts,
    };
    return this.apiService.post(url, body);
  }

  activateAddonBulkAction(
    listId: string,
    productId: string,
    productName: string,
    numAccounts: number,
    addonId: string,
    addonName: string,
  ): Observable<null | never> {
    const addon: AddonInfo = { addonId: addonId, addonName: addonName };
    return this.activateAppBulkAction(listId, productId, productName, numAccounts, undefined, addon);
  }

  /**
   * @deprecated use changeMarketBulkAction exclusively once `bulk_actions_v2` feature is GA
   */
  public changeMarketForList(listId: string, market: Market): Observable<any> {
    const url = '/_ajax/v1/action-lists/change-market/';
    const body = {
      marketId: market.market_id,
      marketName: market.name,
    } as any;
    if (listId.startsWith('G-')) {
      body.groupId = listId;
    } else {
      body.actionListId = listId;
    }
    return this.apiService.post(url, body);
  }

  changeMarketBulkAction(listId: string, market: Market): Observable<null | never> {
    const url = '/_ajax/v1/bulk-actions/start-pipeline/change-market/';
    const body: ChangeMarketRequest = {
      actionListId: listId,
      newMarketId: market.market_id,
      newMarketName: market.name,
    };
    return this.apiService.post(url, body);
  }

  /**
   * @deprecated use addTagsBulkAction exclusively once `bulk_actions_v2` feature is GA
   */
  public addTagsToList(listId: string, tags: string[]): Observable<any> {
    const url = '/_ajax/v1/action-lists/add-tags/';
    const body = {
      tags: tags,
    } as any;
    if (listId.startsWith('G-')) {
      body.groupId = listId;
    } else {
      body.actionListId = listId;
    }
    return this.apiService.post(url, body);
  }

  addTagsBulkAction(listId: string, tags: string[], marketId?: string): Observable<null | never> {
    const url = '/_ajax/v1/bulk-actions/start-pipeline/add-tags/';
    const body: AddTagsRequest = {
      actionListId: listId,
      tags: tags,
    };
    if (marketId) {
      body.marketId = marketId;
    }
    return this.apiService.post(url, body);
  }

  /**
   * @deprecated use createBrandReportFromList exclusively once `bulk_actions_v2` feature is GA
   */
  public createBrandReportFromLegacyList(listId: string, reportName: string, organizeBy: string): Observable<any> {
    const url = '/_ajax/v1/action-lists/create-brand/';
    const body = {
      actionListId: listId,
      brandName: reportName,
      brandCreateOption: organizeBy,
    };
    return this.apiService.post(url, body);
  }

  createBrandReportFromList(
    listId: string,
    listName: string,
    reportName: string,
    organizeBy: BrandCreateOption,
  ): Observable<null | never> {
    const url = '/_ajax/v1/bulk-actions/start-pipeline/create-brand/';
    const body: CreateBrandRequest = {
      actionListId: listId,
      actionListName: listName,
      brandName: reportName,
      brandCreateOption: organizeBy,
    };
    return this.apiService.post(url, body);
  }

  scheduleCampaignForUserList(
    partnerId: string,
    userListId: string,
    campaignId: string,
    scheduleOn: Date,
    timezone: string,
    defaultAccountGroup: string,
    email?: string,
    name?: string,
  ): Observable<string> {
    const dateWithoutTimezone = moment(scheduleOn).format('YYYY-MM-DD[T]HH:mm:ss');
    scheduleOn = moment.tz(dateWithoutTimezone, timezone).toDate();
    return this.bulkProcessor.scheduleCampaign(
      partnerId,
      userListId,
      campaignId,
      scheduleOn,
      defaultAccountGroup,
      email,
      name,
    );
  }
}
